import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Filter, Star, BookOpen, Clock, Video, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getSportEmoji, getCategoryEmoji, getEmojiStyle,
         SquashRacquetIcon, RacquetballRacquetIcon, PadelRacquetIcon, PickleballPaddleIcon,
         JavelinIcon, ShotPutIcon, HammerThrowIcon, DivingIcon, HighJumpIcon, PoleVaultIcon } from '../utils/sportIcons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEVELS = [
  { id: 'all', name: 'All Levels' },
  { id: 'beginner', name: 'Beginner' },
  { id: 'advanced', name: 'Advanced' },
  { id: 'professional', name: 'Professional' },
];

function BrowseCourses() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sportsCategories, setSportsCategories] = useState([]);
  const [filters, setFilters] = useState({
    sport: searchParams.get('sport') || 'all',
    level: searchParams.get('level') || 'all',
  });
  
  const fetchSportsCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sports/categories`);
      setSportsCategories(response.data);
    } catch (error) {
      console.error('Error fetching sports categories:', error);
    }
  }, []);
  
  const fetchCourses = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.sport && filters.sport !== 'all') params.append('sport', filters.sport);
      if (filters.level && filters.level !== 'all') params.append('level', filters.level);
      
      const response = await axios.get(`${API}/courses?${params.toString()}`);
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);
  
  useEffect(() => {
    fetchSportsCategories();
  }, [fetchSportsCategories]);
  
  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);
  
  const handleFilterChange = useCallback((key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // Update URL
    const params = new URLSearchParams();
    if (newFilters.sport && newFilters.sport !== 'all') params.set('sport', newFilters.sport);
    if (newFilters.level && newFilters.level !== 'all') params.set('level', newFilters.level);
    setSearchParams(params);
  }, [filters, setSearchParams]);
  
  return (
    <div className="min-h-screen bg-deep-dark py-12" data-testid="browse-courses-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <span className="inline-block px-4 py-1 bg-premium-gold/20 text-premium-gold rounded-full text-sm font-semibold tracking-wider mb-4">
            Training Programs
          </span>
          <h1 
            className="text-4xl md:text-5xl font-bold tracking-tight text-white"
            style={{ fontFamily: 'Fraunces, serif' }}
            data-testid="page-title"
          >
            Browse <span className="gold-gradient">courses</span>
          </h1>
        </div>
        
        {/* Filters */}
        <div className="bg-dark-surface p-6 mb-8 rounded-xl border border-premium-gold/20" data-testid="filters-section">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
            <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Fraunces, serif' }}>
              Filters
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-soft-lavender mb-2">Sport</label>
              <Select value={filters.sport} onValueChange={(value) => handleFilterChange('sport', value)}>
                <SelectTrigger className="w-full bg-deep-dark border-premium-gold/30 text-white" data-testid="sport-filter">
                  <SelectValue placeholder="All Sports" />
                </SelectTrigger>
                <SelectContent className="max-h-80 bg-dark-surface border-premium-gold/30">
                  <SelectItem value="all" className="text-white hover:bg-deep-dark/50">All Sports</SelectItem>
                  {sportsCategories.map((category) => {
                    const categoryEmoji = getCategoryEmoji(category.category_id);
                    const isIndoorCourt = category.category_id?.toLowerCase().includes('indoor') || 
                                         category.category_id?.toLowerCase().includes('court');
                    const categoryStyle = isIndoorCourt ? {} : getEmojiStyle();
                    
                    return (
                      <React.Fragment key={category.category_id}>
                        <div className="px-2 py-1.5 text-xs font-bold text-premium-gold bg-deep-dark/50 tracking-wider flex items-center gap-2">
                          <span style={categoryStyle}>{categoryEmoji}</span>
                          {category.category_name}
                        </div>
                        {category.sports.map((sport) => {
                          const sportIcon = getSportEmoji(sport.id, sport.name);
                          const sportStyle = getEmojiStyle(sport.id);
                          
                          // Map custom SVG names to components
                          const customIcons = {
                            'SquashRacquet': SquashRacquetIcon,
                            'RacquetballRacquet': RacquetballRacquetIcon,
                            'PadelRacquet': PadelRacquetIcon,
                            'PickleballPaddle': PickleballPaddleIcon,
                            'Javelin': JavelinIcon,
                            'ShotPut': ShotPutIcon,
                            'HammerThrow': HammerThrowIcon,
                            'Diving': DivingIcon,
                            'HighJump': HighJumpIcon,
                            'PoleVault': PoleVaultIcon
                          };
                          
                          const IconComponent = customIcons[sportIcon];
                          
                          return (
                            <SelectItem key={sport.id} value={sport.id} className="text-white hover:bg-deep-dark/50">
                              <div className="flex items-center gap-2">
                                {IconComponent ? (
                                  <div className="text-premium-gold">
                                    <IconComponent />
                                  </div>
                                ) : (
                                  <span style={sportStyle}>{sportIcon}</span>
                                )}
                                {sport.name}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-soft-lavender mb-2">Level</label>
              <Select value={filters.level} onValueChange={(value) => handleFilterChange('level', value)}>
                <SelectTrigger className="w-full bg-deep-dark border-premium-gold/30 text-white" data-testid="level-filter">
                  <SelectValue placeholder="All Levels" />
                </SelectTrigger>
                <SelectContent className="bg-dark-surface border-premium-gold/30">
                  {LEVELS.map((level) => (
                    <SelectItem key={level.id} value={level.id} className="text-white hover:bg-deep-dark/50">{level.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        
        {/* Courses Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-3 border-premium-gold/20 border-t-premium-gold"></div>
          </div>
        ) : courses.length === 0 ? (
          <div className="text-center py-16 bg-dark-surface rounded-xl border border-premium-gold/20" data-testid="no-courses-message">
            <BookOpen className="w-16 h-16 text-premium-gold/30 mx-auto mb-4" strokeWidth={1.5} strokeWidth={1.5} />
            <p className="text-xl text-white/60">No courses found matching your filters.</p>
            <p className="text-white/40 mt-2">Try adjusting your filters or check back later.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="courses-grid">
            {courses.map((course) => (
              <Link 
                key={course.course_id} 
                to={`/courses/${course.course_id}`}
                className="course-card bg-dark-surface rounded-xl overflow-hidden group border border-premium-gold/20 hover:border-premium-gold/50"
                data-testid={`course-card-${course.course_id}`}
              >
                <div className="h-48 bg-deep-dark overflow-hidden relative">
                  {course.image_url ? (
                    <img 
                      src={course.image_url} 
                      alt={course.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-deep-dark to-dark-surface">
                      <span className="text-4xl font-black text-white/20 uppercase" style={{ fontFamily: 'Fraunces, serif' }}>
                        {course.sport_category}
                      </span>
                    </div>
                  )}
                  {/* Level Badge */}
                  <div className="absolute top-4 left-4">
                    <span className="text-xs font-bold tracking-wide text-deep-dark bg-premium-gold px-3 py-1 rounded-full">
                      {course.level}
                    </span>
                  </div>
                  {/* Rating */}
                  {course.rating > 0 && (
                    <div className="absolute top-4 right-4 flex items-center gap-1 bg-dark-surface/80 backdrop-blur px-2 py-1 rounded-full">
                      <Star className="w-4 h-4 text-premium-gold fill-current" strokeWidth={1.5} strokeWidth={1.5} />
                      <span className="text-sm font-bold text-premium-gold">{course.rating.toFixed(1)}</span>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-white group-hover:text-premium-gold transition-colors" style={{ fontFamily: 'Fraunces, serif' }}>
                    {course.title}{course.age_group && course.age_group !== 'All ages' ? ` (${course.age_group})` : ''}
                  </h3>
                  <p className="text-sm text-white/60 mb-4 line-clamp-2">
                    {course.description}
                  </p>
                  {/* Coach Info */}
                  <div className="flex items-center gap-3 mb-4 pb-4 border-b border-premium-gold/20">
                    {course.coach_picture ? (
                      <img src={course.coach_picture} alt={course.coach_name} className="w-10 h-10 rounded-full border-2 border-premium-gold/50" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-deep-dark flex items-center justify-center border-2 border-premium-gold/30">
                        <Users className="w-5 h-5 text-premium-gold/50" strokeWidth={1.5} strokeWidth={1.5} />
                      </div>
                    )}
                    <div>
                      <span className="text-sm font-semibold text-white block">{course.coach_name || 'Coach'}</span>
                      {course.coach_rating > 0 && (
                        <span className="text-xs text-premium-gold">⭐ {course.coach_rating.toFixed(1)} rating</span>
                      )}
                    </div>
                  </div>
                  {/* Price & Duration */}
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-2xl font-black text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                        ₹{course.price.toFixed(0)}
                      </span>
                      <span className="text-sm text-white/50 ml-1">/ {course.duration_weeks} weeks</span>
                    </div>
                  </div>
                  {/* Features Tags */}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {course.includes_live_sessions && (
                      <span className="text-xs px-2 py-1 bg-premium-gold/20 text-premium-gold rounded-full font-medium flex items-center gap-1">
                        <Video className="w-3 h-3" strokeWidth={1.5} strokeWidth={1.5} /> Live
                      </span>
                    )}
                    {course.includes_recorded_videos && (
                      <span className="text-xs px-2 py-1 bg-premium-gold/20 text-premium-gold rounded-full font-medium flex items-center gap-1">
                        <Clock className="w-3 h-3" strokeWidth={1.5} strokeWidth={1.5} /> Recorded
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default BrowseCourses;
